import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {CreateEventFormConfirmationValidationSchema} from '@/features/agenda/formSchemas'
import {HttpCreateEventOptions} from '@/features/agenda/http/agenda.http'
import {useStoreEvent} from '@/features/agenda/queries/useStoreEvent'
import {eventFormValuesToCreatePayload, eventTypeMapToLabel} from '@/features/agenda/utils'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {DialogHeading, DialogStore, useDialogContext} from '@ariakit/react'
import {FormProvider, SubmitErrorHandler, useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'

export const AgendaCreateEventConfirmationModal = ({
    confirmationDialog,
    defaultValues
}: {
    confirmationDialog: DialogStore
    defaultValues: CreateEventFormConfirmationValidationSchema
}) => {
    const {t} = useTranslation()
    const form = useForm<CreateEventFormConfirmationValidationSchema>({
        resolver: zodResolver(CreateEventFormConfirmationValidationSchema),
        defaultValues: {
            ...defaultValues,
            notificationsRecipients: defaultValues.consultants.map(c => ({id: c.id, fullName: c.fullName}))
        }
    })
    const dialog = useDialogContext()
    const queryClient = useQueryClient()

    const projectIds = useWatch({control: form.control, name: 'patients'})
        .map(p => p.project?.id)
        .filter(Boolean)
    const watchPatients = useWatch({control: form.control, name: 'patients'})
    const watchIsGroupTreatment = useWatch({control: form.control, name: 'isGroupTreatment'})
    const watchConsultants = useWatch({control: form.control, name: 'consultants'})

    const accessTypes = watchIsGroupTreatment
        ? t('agenda:group_treatment')
        : watchConsultants
          ? watchConsultants
                ?.filter(
                    (ec, index, self) =>
                        index === self.findIndex(e => e.accessType?.accessType === ec.accessType?.accessType)
                )
                ?.map(ec => (ec.accessType?.accessType ? t(eventTypeMapToLabel[ec.accessType.accessType]) : ''))
                .join(', ')
          : t('agenda:unknown_event_name')

    const fullNames = watchPatients
        ? watchPatients.length > 2
            ? `${watchPatients[0].fullName}, ${watchPatients[1].fullName} +${watchPatients.length - 2}`
            : watchPatients.map(ec => ec.fullName).join(', ')
        : ''

    const {mutate: createEvent, isPending: isCreatingEvent} = useStoreEvent({
        onSuccess: events => {
            toast.success(t('agenda:event_created_successfully'))
            const hasOpeningProcessingEvents = events.some(event =>
                event?.eventConsultants?.some(
                    consultant =>
                        consultant.accessType?.accessType == 'cycle_opening_processing' ||
                        consultant.accessType?.accessType == 'project_opening_processing'
                )
            )

            if (hasOpeningProcessingEvents) {
                projectIds.forEach(projectId => {
                    queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, projectId]})
                })
            }

            dialog?.hide()
        }
    })

    const handleCreateEvent = (formValues: CreateEventFormConfirmationValidationSchema) => {
        const payload: HttpCreateEventOptions['payload'] = eventFormValuesToCreatePayload(formValues)
        createEvent(payload)
    }

    const handleCreateEventError: SubmitErrorHandler<CreateEventFormConfirmationValidationSchema> = errors => {
        console.error(errors, {values: form.getValues()})
    }

    const onSubmit = form.handleSubmit(handleCreateEvent, handleCreateEventError)

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={confirmationDialog}
                onConfirm={onSubmit}
                isConfirmPending={isCreatingEvent}
                headerSlot={
                    <Flexbox direction="column" gap={2}>
                        <DialogHeading>{t('agenda:create_event')}</DialogHeading>
                        <Text palette="neutral" shade="600" size="textSm">
                            {`${fullNames} - ${accessTypes}`}
                        </Text>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
